import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function StandUpPaddleboarding() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'I have never done SUP before. How hard is it?', answer: 'SUP is surprisingly easy! Most beginners are standing and paddling within 10-15 minutes. Our calm water locations and patient instruction make it accessible for nearly everyone.' },
    { question: 'What if I fall in the water?', answer: 'Falling is part of the fun! The Nile water is warm and you will be wearing a life jacket. Simply swim back to your board, grab the handle, and pull yourself back up.' },
    { question: 'Do I need to be a strong swimmer?', answer: 'Basic swimming ability is required for safety. You will wear a life jacket throughout, and we stay in calm sections away from currents.' },
    { question: 'What is the minimum age?', answer: 'Children aged 10+ can paddle independently with parental consent. Younger children (6-9) can ride tandem on boards with parents.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Stand-Up Paddleboarding
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Glide across calm Nile waters at your own peaceful pace
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(64 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-droplet" className="w-4 h-4 mr-2" />
                Calm Waters
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                Easy to Learn
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-calendar-check" className="w-4 h-4 mr-2" />
                Free Cancellation
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your SUP Session
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Find Your Balance on the World's Longest River
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  If you are looking for a water activity that combines gentle adventure with meditative calm, stand-up paddleboarding (SUP) on the Nile offers exactly that perfect balance. Unlike the high-octane thrills of rafting or the technical demands of kayaking, SUP lets you glide across glassy waters at your own contemplative pace, surrounded by stunning scenery and the soothing sounds of nature. It is deceptively simple—stand on a large stable board, hold a long paddle, and propel yourself forward—yet remarkably rewarding once you find your rhythm.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Most beginners are standing and paddling within 15 minutes, and the calm sections of the Nile near Jinja provide ideal conditions for first-timers to build confidence without battling currents or waves. Sessions begin at a peaceful riverside launch site, where instructors fit participants with life jackets and provide lightweight paddles. After a brief shore-based demonstration of proper technique, participants kneel on their boards in shallow water and practice a few strokes before attempting to stand.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The routes we follow are carefully chosen to showcase Jinja's aquatic beauty while remaining protected from strong currents. You will paddle past riverbanks draped in papyrus and water lilies, where egrets and kingfishers hunt in the shallows and monitor lizards sun themselves on fallen logs. The perspective from a SUP board is wonderfully intimate—you are close enough to the water to watch tilapia swim beneath your board, yet elevated enough to spot distant hippos grazing along the shoreline.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Paddle With Us
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-droplet', title: 'Calm Protected Waters', description: 'Gentle sections away from rapids, perfect for building confidence safely' },
                  { icon: 'fa-heart', title: 'Easy for Beginners', description: 'Most people stand and paddle within 15 minutes with our instruction' },
                  { icon: 'fa-shield-halved', title: 'Safety Kayak Support', description: 'Instructor accompanies in kayak providing guidance and assistance throughout' },
                  { icon: 'fa-eye', title: 'Wildlife Viewing', description: 'Spot kingfishers, herons, monitor lizards, and distant hippos from water level' },
                  { icon: 'fa-sun', title: 'Morning & Afternoon', description: 'Choose cooler morning calm or golden-hour afternoon paddling sessions' },
                  { icon: 'fa-users', title: 'Small Group Sizes', description: 'Maximum 6 paddlers ensures personalized instruction and flexible pacing' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Session Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences
                  and pickup location. We can adjust timing to suit your schedule.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '08:00', title: 'Arrival & Equipment', desc: 'Check in at riverside base, get fitted with life jacket, receive board and paddle.' },
                  { time: '08:15', title: 'Shore Instruction', desc: 'Learn paddling technique, balance tips, and safety protocols on land first.' },
                  { time: '08:30', title: 'Water Introduction', desc: 'Start kneeling in shallow water, practice strokes, then attempt standing.' },
                  { time: '09:00', title: 'Guided Paddle', desc: 'Explore calm river sections with instructor support, building confidence gradually.' },
                  { time: '09:45', title: 'Wildlife & Photos', desc: 'Drift near papyrus banks observing birds, take photos from unique perspective.' },
                  { time: '10:00', title: 'Return & Finish', desc: 'Paddle back to launch site, share experiences over refreshments.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Swimsuit and towel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Quick-dry shirt (UV protection)</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                    Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof sunscreen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunglasses with strap</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof phone case</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Frequently Asked Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Water Fun
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/kayaking-adventures" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800"
                      alt="Kayaking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kayaking Adventures</h3>
                    <p className="text-gray-700 mb-4">Paddle the Nile at your own pace.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/river-tubing" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800"
                      alt="River Tubing"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">River Tubing</h3>
                    <p className="text-gray-700 mb-4">Lazy float down the Nile.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 2-3 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 10+ years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your SUP Session
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "SUP board and paddle",
                    "Life jacket (PFD)",
                    "Instructor & safety kayak",
                    "Drinking water",
                    "Dry bag"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to launch site",
                    "Personal insurance",
                    "Towel and clothes",
                    "Tips for instructors"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Swimming:</strong> Basic ability required.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Gear:</strong> Life jackets mandatory.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Stand-Up Paddleboarding"
      />
    </div>
  )
}
